import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { playAcknowledgement } from "../audio";
import {
  SLOP_FRESH,
  SLOP_HEAD,
  SLOP_HOLD_DRIFT,
  SLOP_HOLD_MS,
  SLOP_RESET_A11Y,
  SLOP_RESET_LABEL,
  TALLY_GROUP,
  slopBest,
  slopBestLine,
  slopConfirmMessage,
  slopDays,
  slopHeldLabel,
  slopResetParts,
  slopSignLabel,
  tallyGroupRects,
  tallyGroups,
  tallyOverflow,
  type SlopTally,
} from "../../shared/slop";
import { PixelIcon } from "./PixelArt";
import { MentionText } from "./MentionText";
import { BusyLabel } from "./BusyLabel";
import { ErrorBox } from "./Shell";

/** One group of tally marks (shared/slop.ts tallyGroupRects): ink rects at 2, crisp, like the stopwatch. */
export function TallyGroup({ marks }: { marks: number }) {
  const { width, height, scale } = TALLY_GROUP;
  return (
    <svg class="slop-group" width={width * scale} height={height * scale} viewBox={`0 0 ${width} ${height}`} shape-rendering="crispEdges" aria-hidden="true" focusable="false">
      {tallyGroupRects(marks).map((r) => (
        <rect key={`${r.x}-${r.y}`} x={r.x} y={r.y} width={r.w} height={r.h} />
      ))}
    </svg>
  );
}

/**
 * The slop tally (docs/notes.md "The slop tally", flag slopTallyUI; owner, 2026-10-23: "The board
 * should have a “days without Omie’s slop” tally in tally style. Anyone can reset it but there is
 * confirmation to do so"): a workplace sign pinned straight across the top of the cork, above the
 * slips' columns. The heading, the days as ink tally marks alone (fives slashed, thirty at most, "+34"
 * after them past that), and under them the best run and who reset it. Reset, for anyone, asks first
 * with the browser's own dialog, as every web confirm does.
 *
 * The hold (owner, 2026-10-26: "Drop the plate, press and hold will show the digit count and it will
 * disappear when let go"): a pointer held still on the paper (not on Reset) for SLOP_HOLD_MS shows the
 * count ("23 days") in its ink plate over the marks' row, with no sound; up, cancel (a scroll took the
 * touch) or leave puts the marks back. Not on pointer down: a scroll that starts on the sign drifts
 * past SLOP_HOLD_DRIFT or is cancelled before the wait is up, so it never blinks the count (owner,
 * 2026-10-30: "I don't like how the number flashes briefly when scrolling on the days without
 * slop"). The marks keep their room while hidden, so the sign never changes height. The phone's own
 * hold menu and text selection stay off the paper, as on the slips.
 * A screen reader has the count in the sign's name already, so the hold is a sighted shortcut.
 */
export function SlopSign({ tally, now, onReset }: { tally: SlopTally; now: number; onReset: (tally: SlopTally) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [held, setHeld] = useState(false);
  // The hold waiting to show the count: its timer and where the pointer went down.
  const pending = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
  const days = slopDays(tally.reset_at, now, tally.tz);
  const best = slopBest(tally.best_days, days);
  const bestLine = slopBestLine(best);
  const who = slopResetParts(tally);
  const groups = tallyGroups(days);
  const over = tallyOverflow(days);

  function stopWaiting() {
    if (pending.current) clearTimeout(pending.current.timer);
    pending.current = null;
  }
  function letGo() {
    stopWaiting();
    setHeld(false);
  }
  useEffect(() => stopWaiting, []);

  async function reset() {
    if (busy || !confirm(slopConfirmMessage(days))) return;
    setBusy(true);
    setError(null);
    try {
      onReset(await api.slop.reset());
      void playAcknowledgement();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="slop-sign" data-days={days}>
      <PixelIcon name="pinned" className="slop-pin left" />
      <PixelIcon name="pinned" className="slop-pin right" />
      <div
        class={`slop-paper${held ? " held" : ""}`}
        role="group"
        aria-label={slopSignLabel(days, best, tally)}
        onPointerDown={(e) => {
          if (e.button !== 0 || (e.target as Element).closest?.(".slop-reset")) return;
          stopWaiting();
          const timer = setTimeout(() => {
            pending.current = null;
            setHeld(true);
          }, SLOP_HOLD_MS);
          pending.current = { timer, x: e.clientX, y: e.clientY };
        }}
        onPointerMove={(e) => {
          const p = pending.current;
          if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > SLOP_HOLD_DRIFT) stopWaiting();
        }}
        onPointerUp={letGo}
        onPointerCancel={letGo}
        onPointerLeave={letGo}
        onContextMenu={(e) => e.preventDefault()}
      >
        <span class="slop-head" aria-hidden="true">
          {SLOP_HEAD}
        </span>
        <span class="slop-count" aria-hidden="true">
          {groups.length > 0 ? (
            <span class="slop-marks">
              {groups.map((n, i) => (
                <TallyGroup key={i} marks={n} />
              ))}
              {over > 0 && <span class="slop-more">+{over}</span>}
            </span>
          ) : (
            <span class="slop-fresh">{SLOP_FRESH}</span>
          )}
          {held && (
            <span class="slop-held">
              <span class="slop-number">{slopHeldLabel(days)}</span>
            </span>
          )}
        </span>
        <span class="slop-foot">
          <span class="slop-meta" aria-hidden="true">
            {bestLine && <span class="slop-best">{bestLine}</span>}
            {who.before && (
              <span class="slop-by">
                {who.before}
                {who.name && <MentionText text={who.name} />}
                {who.after}
              </span>
            )}
          </span>
          <button type="button" class="btn-quiet slop-reset" aria-label={SLOP_RESET_A11Y} disabled={busy} onClick={() => void reset()}>
            <BusyLabel busy={busy}>{SLOP_RESET_LABEL}</BusyLabel>
          </button>
        </span>
      </div>
      {error && <ErrorBox message={error} />}
    </div>
  );
}
